import React from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import moment from 'moment'
import { noop } from 'lodash-es'
import {
  DealActions,
  DealDetail,
  DealServicingStartReturnRecallArgs,
  DealStatus,
  NegotiationFieldMeta,
  UserPreference,
  ValidationIssue
} from 'api'
import { Groupbox, ModalContent, ModalFooter, ModalWindow } from 'view/components/panels'
import { ValidationIssuesContainer } from 'view/shared/error'
import { DialogField, DialogRow, NumericInputField, TextInputField } from 'view/components/inputs'
import { DecisionMakerSelectboxField, TradingDatePickerField } from '../entity'
import { HaircutTypeSelectboxField } from '../entity/haircutOptions/HaircutTypeSelectbox'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { getHaircutFormatOptions, getNotionalFormatOptions, getRateFormatOptions, getStartConsiderationFormatOptions } from '../datagrid'
import { createInvalidValueValidationIssue, getDateEditorFormat, Severity } from 'model'
import { DateLike, notNilFilter } from 'utils'
import { createDealDetailSelector } from 'selectors/dealSelectors'
import { platformTradeDateSelector } from 'selectors/platformSelectors'
import { createUserPreferenceSelector } from 'selectors/userPreferenceSelectors'
import { Locale, Translation } from 'locale'
import { canServicingCancel, canServicingSend, getDealModificationColour, getNegotiatedValue } from 'view/routes/deals/components/utils'
import { DealFieldNames } from 'view/routes/deals/components/DealsGridColumnNames'
import { getEndDateModificationType, getStringModificationType } from 'view/routes/requests/shared/deltaGetters'
import { CallableTypeSelectboxField } from '../entity/CallableTypeSelectbox'
import { useOnMount } from 'view/hooks'
import { useGetEffectiveDateInfo } from './useGetEffectiveDateInfo'

export type ServicingReturnRecallDialogProps = OwnProps & StateProps & DispatchProps

interface OwnProps {
  dealId: string
  onClose?: () => unknown
}

interface StateProps {
  dealDetail: DealDetail | undefined
  platformTradeDate: DateLike | null
  userPreference: UserPreference
}

interface DispatchProps {
  onServicingStartReturnRecall: (args: DealServicingStartReturnRecallArgs) => unknown
  onUpdateField: typeof DealActions.servicingUpdateAttributes
  onRevert: typeof DealActions.servicingRevertAttributes
  onSend: typeof DealActions.servicingSend
  onCancel: typeof DealActions.servicingCancel
}

const dealIdSelector = (_: {}, props: ServicingReturnRecallDialogProps) => props.dealId

const mapStateToProps = createStructuredSelector({
  dealDetail: createDealDetailSelector(dealIdSelector),
  platformTradeDate: platformTradeDateSelector,
  userPreference: createUserPreferenceSelector()
})

const mapDispatchToProps = {
  onServicingStartReturnRecall: DealActions.servicingStartReturnRecall,
  onUpdateField: DealActions.servicingUpdateAttributes,
  onRevert: DealActions.servicingRevertAttributes,
  onCancel: DealActions.servicingCancel,
  onSend: DealActions.servicingSend
}

export const ServicingReturnRecallDialogComponent: React.FunctionComponent<ServicingReturnRecallDialogProps> = props => {
  const {
    dealId,
    dealDetail,
    platformTradeDate,
    onServicingStartReturnRecall,
    onClose,
    userPreference,
    onCancel,
    onSend,
    onUpdateField
  } = props

  const getEffectiveDateInfo = useGetEffectiveDateInfo()

  useOnMount(() => {
    if (dealDetail && dealDetail.deal.status !== DealStatus.SERVICING_WITH_INITIATOR) {
      onServicingStartReturnRecall({
        dealId: dealDetail.deal.dealId
      })
    }
  })

  if (!dealDetail) return null

  const { deal, collateral, collateralBasket, referenceRate, servicingSideDetails, ownOrder } = dealDetail

  const negotiationFields = servicingSideDetails?.negotiationFields
  const sideFields = servicingSideDetails?.sideFields

  const [, minEffectiveDate, maxEffectiveDate, isBeforeCutOffTime] = getEffectiveDateInfo(dealDetail)

  const effectiveDate = getNegotiatedValue(servicingSideDetails?.negotiationFields[NegotiationFieldMeta.END_DATE]?.negotiableFieldRequired)

  const clientValidationIssues: ValidationIssue[] = [
    platformTradeDate && effectiveDate && moment(effectiveDate).isSame(platformTradeDate, 'day') && !isBeforeCutOffTime
      ? createInvalidValueValidationIssue('endDate', Locale.translate(Translation.SC_EFFECTIVE_DATE_TODAY_WARNING_MESSAGE), Severity.WARN)
      : null
  ].filter(notNilFilter)

  const validationIssues = (servicingSideDetails?.validationIssues ?? []).concat(clientValidationIssues)
  const hasValidationErrors = validationIssues.filter(validationIssue => validationIssue.severity === Severity.ERROR).length > 0

  const notionalFormatOptions = getNotionalFormatOptions(userPreference.amountFormat)
  const startConsiderationFormatOptions = getStartConsiderationFormatOptions()
  const rateFormatOptions = getRateFormatOptions(referenceRate)
  const haircutFormatOptions = getHaircutFormatOptions(undefined, userPreference.haircutSign, ownOrder, deal.direction)
  const dateEditorFormat = getDateEditorFormat(userPreference.dateFormat)

  const handleUpdateField = (value: any, field: NegotiationFieldMeta, rawValue?: any) => {
    onUpdateField({
      dealId,
      field,
      updatedValue: value,
      updatedRawValue: rawValue,
      decisionMakerOrientedByNegotiationSide: true,
      meta: { haircutSign: userPreference.haircutSign },
      revertFirst: false
    })
  }

  const handleSend = () => {
    onSend(dealDetail.deal.dealId)
    onClose?.()
  }

  const handleCancel = async () => {
    if (canServicingCancel(dealDetail)) {
      onCancel(dealDetail.deal.dealId)
    }
    onClose?.()
  }

  return (
    <ModalWindow title={Locale.translate(Translation.TC_RETURN_RECALL)} onEscKey={handleCancel}>
      <ModalContent>
        <Groupbox>
          {!isBeforeCutOffTime && (
            <ValidationIssuesContainer>
              {<li>{Locale.translate(Translation.SC_REQUEST_INITIATED_AFTER_CUT_OFF_TIME)}</li>}
            </ValidationIssuesContainer>
          )}
          <DialogRow>
            <DialogField flex={4}>
              <TextInputField
                label={Locale.translate(Translation.TC_COLLATERAL)}
                value={collateral?.name ?? collateralBasket?.name}
                readOnly={true}
                onUpdate={noop}
              />
            </DialogField>
            <DialogField flex={4}>
              <NumericInputField
                label={Locale.translate(Translation.TC_NOTIONAL, { amountFormat: userPreference.amountFormat })}
                formatOptions={notionalFormatOptions}
                value={deal.notional}
                readOnly={true}
                onUpdate={noop}
              />
            </DialogField>
            <DialogField flex={4}>
              <NumericInputField
                label={Locale.translate(Translation.TC_START_CONSIDERATION)}
                formatOptions={startConsiderationFormatOptions}
                value={deal.startConsideration}
                readOnly={true}
                onUpdate={noop}
              />
            </DialogField>
            <DialogField flex={4}>
              <NumericInputField
                label={Locale.translate(Translation.TC_RATE)}
                formatOptions={rateFormatOptions}
                value={deal.rate}
                readOnly={true}
                onUpdate={noop}
              />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField flex={4}>
              <NumericInputField
                label={Locale.translate(Translation.TC_HAIRCUT)}
                formatOptions={haircutFormatOptions}
                value={deal.haircut}
                readOnly={true}
                onUpdate={noop}
                dataTestId="ServicingSubstitutionDialog-HaircutNumericInputField"
              />
            </DialogField>
            <DialogField flex={4}>
              <HaircutTypeSelectboxField
                label={Locale.translate(Translation.TC_HAIRCUT_TYPE)}
                value={deal.haircutType}
                readOnly={true}
                onUpdate={noop}
              />
            </DialogField>
            <DialogField flex={4}>
              <CallableTypeSelectboxField
                label={Locale.translate(Translation.TC_CALLABLE)}
                value={deal.callableType}
                readOnly={true}
                onUpdate={noop}
              />
            </DialogField>
            <DialogField flex={4}>
              <NumericInputField
                label={Locale.translate(Translation.TC_CALLABLE_PERIOD)}
                value={deal.callablePeriodDays}
                readOnly={true}
                onUpdate={noop}
              />
            </DialogField>
          </DialogRow>
        </Groupbox>
        <Groupbox>
          <DialogRow>
            <DialogField flex={3}>
              <TradingDatePickerField
                label={Locale.translate(Translation.SC_EFFECTIVE_DATE)}
                fieldName={NegotiationFieldMeta.END_DATE}
                currency={collateral?.currency}
                minDate={minEffectiveDate ?? undefined}
                maxDate={maxEffectiveDate ?? undefined}
                dateFormat={dateEditorFormat}
                validationField={DealFieldNames.DEAL_END_DATE}
                validationIssues={validationIssues}
                underline={getDealModificationColour(
                  () => getEndDateModificationType(negotiationFields?.END_DATE?.negotiableFieldRequired),
                  dealDetail
                )}
                value={effectiveDate}
                onUpdate={(value: string | null) => handleUpdateField(value, NegotiationFieldMeta.END_DATE)}
                dataTestId={'ServicingReturnRecallDialog-EffectiveDateTradingDatePickerField'}
              />
            </DialogField>
            <DialogField flex={3}>
              <DecisionMakerSelectboxField
                label={Locale.translate(Translation.TC_INVESTMENT_DECISION_MAKER)}
                fieldName={
                  dealDetail.ownOrder
                    ? NegotiationFieldMeta.INITIATOR_INVESTMENT_DECISION_MAKER
                    : NegotiationFieldMeta.RESPONDER_INVESTMENT_DECISION_MAKER
                }
                validationField={
                  dealDetail.ownOrder
                    ? DealFieldNames.DEAL_INITIATOR_INVESTMENT_DECISION_MAKER
                    : DealFieldNames.DEAL_RESPONDER_INVESTMENT_DECISION_MAKER
                }
                validationIssues={validationIssues}
                underline={getDealModificationColour(
                  () =>
                    getStringModificationType(
                      sideFields?.[dealDetail.ownOrder ? 'INITIATOR_INVESTMENT_DECISION_MAKER' : 'RESPONDER_INVESTMENT_DECISION_MAKER']
                        ?.negotiableFieldRequired
                    ),
                  dealDetail
                )}
                value={getNegotiatedValue(
                  sideFields?.[dealDetail.ownOrder ? 'INITIATOR_INVESTMENT_DECISION_MAKER' : 'RESPONDER_INVESTMENT_DECISION_MAKER']
                    ?.negotiableFieldRequired
                )}
                onUpdate={handleUpdateField}
                dataTestId={'ServicingReturnRecallDialog-DecisionMakerSelectboxField-Investment'}
              />
            </DialogField>
            <DialogField flex={3}>
              <DecisionMakerSelectboxField
                label={Locale.translate(Translation.TC_EXECUTION_DECISION_MAKER)}
                fieldName={
                  dealDetail.ownOrder
                    ? NegotiationFieldMeta.INITIATOR_EXECUTION_DECISION_MAKER
                    : NegotiationFieldMeta.RESPONDER_EXECUTION_DECISION_MAKER
                }
                validationField={
                  dealDetail.ownOrder
                    ? DealFieldNames.DEAL_INITIATOR_EXECUTION_DECISION_MAKER
                    : DealFieldNames.DEAL_RESPONDER_EXECUTION_DECISION_MAKER
                }
                validationIssues={validationIssues}
                underline={getDealModificationColour(
                  () =>
                    getStringModificationType(
                      sideFields?.[dealDetail.ownOrder ? 'INITIATOR_EXECUTION_DECISION_MAKER' : 'RESPONDER_EXECUTION_DECISION_MAKER']
                        ?.negotiableFieldRequired
                    ),
                  dealDetail
                )}
                value={getNegotiatedValue(
                  sideFields?.[dealDetail.ownOrder ? 'INITIATOR_EXECUTION_DECISION_MAKER' : 'RESPONDER_EXECUTION_DECISION_MAKER']
                    ?.negotiableFieldRequired
                )}
                onUpdate={handleUpdateField}
                dataTestId={'ServicingReturnRecallDialog-DecisionMakerSelectboxField-Execution'}
              />
            </DialogField>
          </DialogRow>
        </Groupbox>
      </ModalContent>
      <ModalFooter>
        <DialogRow>
          <DialogField>
            <TextButton type={TextButtonType.Tertiary} onClick={handleCancel} dataTestId={'ServicingDialog-CancelButton'}>
              {Locale.translate(Translation.TC_BUTTON_CANCEL)}
            </TextButton>
          </DialogField>
          <DialogField flex={0}>
            <TextButton
              type={TextButtonType.Primary}
              disabled={hasValidationErrors || !canServicingSend(dealDetail)}
              onClick={handleSend}
              dataTestId={'ServicingReturnRecallDialog-SendRequestButton'}>
              {Locale.translate(Translation.TC_BUTTON_SEND_REQUEST)}
            </TextButton>
          </DialogField>
        </DialogRow>
      </ModalFooter>
    </ModalWindow>
  )
}

export const ServicingReturnRecallDialog = connect<StateProps, DispatchProps, OwnProps>(
  mapStateToProps,
  mapDispatchToProps
)(ServicingReturnRecallDialogComponent)
